import { fromJson, toJson, type JsonObject, type JsonValue } from "@bufbuild/protobuf";
import { ValueSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import {
	IdentitiesService,
	MemberType,
	type AssignTeamResponse,
	type CancelSharedWorkspaceSetupRequest,
	type CompleteSharedWorkspaceSetupRequest,
	type Corp,
	type CorpFilter,
	type CreateActivationLinkResponse,
	type CreateSCIMConfigurationRequest,
	type CreateSCIMConfigurationResponse,
	type CreateSharedWorkspaceSetupRequest,
	type DeleteTeamResponse,
	type DeleteWorkspaceResponse,
	type Invitation,
	type InvitationJobIdMapping,
	type InvitationV2Identity,
	type ListSharedWorkspaceConnectionsRequest,
	type ListSharedWorkspaceSetupsRequest,
	type ListTeamCandidatesResponse,
	type ListUserCandidatesRequest,
	type ListUserCandidatesResponse,
	type ListWorkspaceTeamsDetails,
	type ListWorkspaceTeamsFilter,
	type ListWorkspaceTeamsResponse,
	type Member,
	type MemberFilter,
	type MemberStatus,
	type Org,
	type OrgFilter,
	type OrgWorkspaceInvitationMapping,
	type RemoveOrgMemberResponse,
	type RemoveTeamMemberResponse,
	type RemoveUserFromWorkspacesResponse,
	type RemoveWorkspaceMemberResponse,
	type Role,
	type RoleCategory,
	type RoleType,
	type SCIMConfiguration,
	type SSOConfigV2Payload,
	type SharedWorkspaceConnection,
	type SharedWorkspaceSetup,
	type SsoConfiguration,
	type SubmitSharedWorkspaceSetupConfigurationRequest,
	type Team,
	type TeamFilter,
	type UnassignTeamResponse,
	type UpdateAssignTeamResponse,
	type User,
	type UserFilter,
	type Workspace,
	type WorkspaceFilter,
	type WorkspaceTeam,
	type WorkspaceTeamAssignmentFilter,
	type WorkspaceVisibility,
} from "#gen/identities/v1/identities_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface OrgList {
	orgs: Org[];
	pageInfo?: PaginationPageInfo;
}

export interface CorpList {
	corps: Corp[];
	pageInfo?: PaginationPageInfo;
}

export interface WorkspaceList {
	workspaces: Workspace[];
	pageInfo?: PaginationPageInfo;
}

export interface UserList {
	users: User[];
	pageInfo?: PaginationPageInfo;
}

export interface TeamList {
	teams: Team[];
	pageInfo?: PaginationPageInfo;
}

export interface WorkspaceTeamList {
	teams: ListWorkspaceTeamsDetails[];
	pageInfo?: PaginationPageInfo;
}

interface MemberList {
	members: Member[];
	pageInfo?: PaginationPageInfo;
}

export interface SSOConfigurationList {
	configs: SsoConfiguration[];
	pageInfo?: PaginationPageInfo;
}

export interface SCIMConfigurationList {
	configs: SCIMConfiguration[];
	pageInfo?: PaginationPageInfo;
}

export interface SharedWorkspaceConnectionList {
	connections: SharedWorkspaceConnection[];
	pageInfo?: PaginationPageInfo;
}

export interface SharedWorkspaceSetupList {
	setups: SharedWorkspaceSetup[];
	pageInfo?: PaginationPageInfo;
}

export class IdentitiesClient {
	private client: Client<typeof IdentitiesService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.posiedon || "/", IdentitiesService, options);
	}

	async getAdminOrg(id: string, userId?: string): Promise<Org> {
		try {
			const res = await this.client.getAdminOrg({ id, userId });
			return res.org!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getUser(userId: string): Promise<User> {
		try {
			const res = await this.client.getUser({ userId });
			return res.user!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getUsers(userIds: string[], options?: CallOptions): Promise<User[]> {
		if (userIds.length === 0) {
			return [];
		}
		try {
			const res = await this.client.getUsersByIds({ ids: userIds }, options);
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listUsers(orgId?: string, filter?: UserFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<UserList> {
		try {
			const res = await this.client.listUsers(
				{
					orgId,
					filter,
					paginate,
				},
				options,
			);
			return { users: res.items!, pageInfo: res.pageInfo } as UserList;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getUserPreferences(): Promise<Record<string, JsonValue>> {
		try {
			const res = await this.client.listUserPreferences({});
			return Object.fromEntries(Object.entries(res.preferences ?? {}).map(([key, value]) => [key, toJson(ValueSchema, value)]));
		} catch (e) {
			throw handleError(e);
		}
	}

	async setUserPreference(key: string, value: JsonValue): Promise<void> {
		try {
			await this.client.setUserPreference({
				key,
				value: fromJson(ValueSchema, value),
			});
		} catch (e) {
			throw handleError(e);
		}
	}

	async listUserCandidates(orgId: string, optionalId?: ListUserCandidatesRequest["optionalId"], filter?: UserFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<ListUserCandidatesResponse> {
		try {
			const res = await this.client.listUserCandidates(
				{
					orgId,
					optionalId,
					filter,
					paginate,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listUsersWithAccess(orgId: string, workspaceId: string, filter?: UserFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<UserList> {
		try {
			const res = await this.client.listUsersWithAccess(
				{
					orgId,
					workspaceId,
					filter,
					paginate,
				},
				options,
			);
			return { users: res.items!, pageInfo: res.pageInfo } as UserList;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createOrUpdateUser(role: RoleType): Promise<void> {
		try {
			await this.client.createOrUpdateUser({ role });
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateUser(user: User, mask: FieldMask): Promise<User> {
		try {
			const { $typeName: _1, email: _, ...rest } = user;
			const res = await this.client.updateUser({ user: rest, mask });
			return res.user!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createInvitation(invitation: { name?: string; email: string; metadata?: JsonObject; phoneNumber?: string; roleIds: string[]; orgId: string; memberStatus: MemberStatus; invitableId: string; type: MemberType }): Promise<Invitation> {
		try {
			const { metadata, ...rest } = invitation;
			const res = await this.client.createInvitation({ ...rest, metadata: JSON.stringify({ ...(metadata || {}), platformSix: true }) });
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createBulkWorkspaceInvitations(invitations: { orgId: string; workspaceId: string; users: { email: string; roleNames: string[] }[] }): Promise<InvitationJobIdMapping[]> {
		try {
			const res = await this.client.createInvitationV2Workspace(invitations);
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createBulkInvitations(invitations: { users: InvitationV2Identity[]; orgRoleNames: string[]; workspaces: OrgWorkspaceInvitationMapping[] }, orgId: string, skipSendEmail?: boolean): Promise<InvitationJobIdMapping[]> {
		try {
			const res = await this.client.createInvitationV2Org({ ...invitations, orgId, skipSendEmail });
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createActivationLink({ userId, orgId }: { userId: string; orgId: string }): Promise<CreateActivationLinkResponse> {
		try {
			const res = await this.client.createActivationLink({ userId, orgId });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getOrg(id: string, userId?: string): Promise<Org> {
		try {
			const res = await this.client.getOrg({ id, userId });
			return res.org!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listOrgs(filter?: OrgFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<OrgList> {
		try {
			const res = await this.client.listOrgs(
				{
					filter,
					paginate,
				},
				options,
			);
			return { orgs: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMyCorps(filter?: CorpFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<CorpList> {
		try {
			const res = await this.client.listMyCorps(
				{
					filter,
					paginate,
				},
				options,
			);
			return { corps: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listCorpOrgs(id: string, paginate?: PaginationArguments): Promise<OrgList> {
		try {
			const res = await this.client.listCorpOrgs({
				id,
				paginate,
			});
			return { orgs: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateOrg(org: Org, mask: FieldMask): Promise<Org> {
		try {
			const res = await this.client.updateOrg({ org, mask });
			return res.org!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getWorkspace(id: string, orgId: string): Promise<Workspace> {
		try {
			const res = await this.client.getWorkspace({ id, orgId });
			return res.workspace!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getWorkspaces(ids: string[], orgId: string, options?: CallOptions): Promise<Workspace[]> {
		try {
			const res = await this.client.getWorkspacesByIds({ ids, orgId }, options);
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listWorkspaces(filter?: WorkspaceFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<WorkspaceList> {
		try {
			const res = await this.client.listWorkspaces({ filter, paginate }, options);
			return { workspaces: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listWorkspacesByOrg(filter?: WorkspaceFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<WorkspaceList> {
		try {
			const res = await this.client.listWorkspacesByOrg({ filter, paginate }, options);
			return { workspaces: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async createWorkspace(
		orgId: string,
		workspaceData: {
			name: string;
			description?: string;
			photoUrl?: string;
			visibility?: WorkspaceVisibility;
		},
	): Promise<Workspace> {
		try {
			const res = await this.client.createWorkspace({
				orgId,
				...workspaceData,
			});
			return res.workspace!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateWorkspace(workspace: Workspace, mask: FieldMask): Promise<Workspace> {
		try {
			const res = await this.client.updateWorkspace({
				workspace,
				mask,
			});
			return res.workspace!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteWorkspace(workspaceId: string, orgId: string): Promise<DeleteWorkspaceResponse> {
		try {
			const res = await this.client.deleteWorkspace({ workspaceId, orgId });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async assignTeamToWorkspace(workspaceId: string, teams: { teamId: string; roleNames: string[] }[]): Promise<AssignTeamResponse> {
		try {
			const res = await this.client.assignTeam({ workspaceId, teams });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async unassignTeamToWorkspace(workspaceId: string, teamId: string): Promise<UnassignTeamResponse> {
		try {
			const res = await this.client.unassignTeam({ workspaceId, teamId });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateAssignTeam(workspaceId: string, teamId: string, roleNames: string[]): Promise<UpdateAssignTeamResponse> {
		try {
			const res = await this.client.updateAssignTeam({ workspaceId, teamId, roleNames });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listTeamCandidates(orgId: string, workspaceId: string, filter?: TeamFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<ListTeamCandidatesResponse> {
		try {
			const res = await this.client.listTeamCandidates(
				{
					orgId,
					workspaceId,
					filter,
					paginate,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateRole(orgId: string, id: string, role: string, memberType: MemberType, _status?: MemberStatus): Promise<Member> {
		try {
			const res = await this.client.updateMember({ orgId, id, role, memberType });
			return res.member!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateTeamRole(workspaceId: string, teamId: string, roleNames: string[]): Promise<WorkspaceTeam> {
		try {
			const res = await this.client.updateAssignTeam({ workspaceId, teamId, roleNames });
			return res.assignment!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteTeam(teamId: string, orgId: string): Promise<DeleteTeamResponse> {
		try {
			const res = await this.client.deleteTeam({ teamId, orgId });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async addWorkspaceMember(members: { userId: string; roleName: string; email: string }[], workspaceId: string): Promise<Member> {
		try {
			const res = await this.client.addWorkspaceMember({ members, workspaceId });
			return res.member!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async addMemberToWorkspaces(workspaces: { role: Role; id: string }[], userId: string, email: string): Promise<Member> {
		try {
			const members = workspaces.map(workspace => ({ userId, roleName: workspace.role.name, email, workspaceId: workspace.id }));
			const res = await this.client.addWorkspaceMember({ members });
			return res.member!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async addTeamMember(members: { userId: string; email: string }[], teamId: string): Promise<Member> {
		try {
			const res = await this.client.addTeamMember({ members, teamId });
			return res.member!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listRoles(roleType?: RoleType, category?: RoleCategory): Promise<Role[]> {
		try {
			const res = await this.client.listRoles({ filter: { roleType, category } });
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCorp(id: string): Promise<Corp> {
		try {
			const res = await this.client.getCorp({ id });
			return res.corp!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createTeam(
		orgId: string,
		teamData: {
			name: string;
			description?: string;
			photoUrl?: string;
		},
	): Promise<Team> {
		try {
			const res = await this.client.createTeam({
				orgId,
				...teamData,
			});
			return res.team!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateTeam(team: Team, mask: FieldMask): Promise<Team> {
		try {
			const res = await this.client.updateTeam({
				team,
				mask,
			});
			return res.team!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMembers(orgId: string, filter?: MemberFilter, paginate?: PaginationArguments): Promise<MemberList> {
		try {
			const res = await this.client.listMembers({ orgId, filter, paginate });
			return { members: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listTeams(orgId: string, filter?: TeamFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<TeamList> {
		try {
			const res = await this.client.listTeams({ orgId, filter, paginate }, options);
			return { teams: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async getTeam(teamId: string, orgId: string): Promise<Team> {
		try {
			const res = await this.client.getTeam({ teamId, orgId });
			return res.team!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async removeTeamMember(userId: string, teamId?: string): Promise<RemoveTeamMemberResponse> {
		try {
			const res = await this.client.removeTeamMember({ teamId, userId });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async removeOrgMember(userId: string, orgId: string): Promise<RemoveOrgMemberResponse> {
		try {
			const res = await this.client.removeOrgMember({ orgId, userId });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async removeWorkspaceMember(userId: string, workspaceId: string): Promise<RemoveWorkspaceMemberResponse> {
		try {
			const res = await this.client.removeWorkspaceMember({ workspaceId, userId });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async removeUserFromWorkspaces(userId: string, workspaceIds: string[]): Promise<RemoveUserFromWorkspacesResponse> {
		try {
			const res = await this.client.removeUserFromWorkspaces({ userId, workspaceIds });
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMembersInWorkspace(id: string, orgId: string, filter?: UserFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<UserList> {
		try {
			const res = await this.client.listUsers(
				{
					orgId: orgId,
					optionalId: {
						case: "workspaceId",
						value: id,
					},
					filter,
					paginate,
				},
				options,
			);
			return { users: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listTeamsInWorkspace(id: string, orgId: string, filter?: ListWorkspaceTeamsFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<ListWorkspaceTeamsResponse> {
		// Drop empty filter to avoid ordering of results changing
		if (filter && Object.keys(filter).length === 0) {
			filter = undefined;
		}

		try {
			const res = await this.client.listWorkspaceTeams(
				{
					workspaceId: id,
					filter,
					paginate,
				},
				options,
			);
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMembersInTeam(id: string, orgId: string, filter?: UserFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<UserList> {
		try {
			const res = await this.client.listUsers(
				{
					orgId: orgId,
					optionalId: {
						case: "teamId",
						value: id,
					},
					filter,
					paginate,
				},
				options,
			);
			return { users: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async createSSOConfiguration(orgId: string, config: SSOConfigV2Payload, metadata?: string, options?: CallOptions): Promise<SsoConfiguration> {
		try {
			const res = await this.client.createSSOConfiguration({ orgId, config, metadata }, options);
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateSSOConfiguration(orgId: string, config: SSOConfigV2Payload, metadata?: string, options?: CallOptions): Promise<SsoConfiguration> {
		try {
			const res = await this.client.updateSSOConfiguration({ orgId, config, metadata }, options);
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listSSOConfigurations(orgId: string, paginate?: PaginationArguments, options?: CallOptions): Promise<SSOConfigurationList> {
		try {
			const res = await this.client.listSSOConfigurations({ orgId, paginate }, options);
			return { configs: res.items!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteSSOConfiguration(orgId: string, configurationId: string): Promise<void> {
		try {
			await this.client.deleteSSOConfiguration({ orgId, configurationId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async listSCIMConfigurations(orgId: string, paginate?: PaginationArguments, options?: CallOptions): Promise<SCIMConfigurationList> {
		try {
			const res = await this.client.listSCIMConfigurations({ orgId, paginate: paginate }, options);
			return { configs: res.configurations!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async createSCIMConfiguration(payload: CreateSCIMConfigurationRequest): Promise<CreateSCIMConfigurationResponse> {
		try {
			return await this.client.createSCIMConfiguration(payload);
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteSCIMConfiguration(orgId: string, configurationId: string): Promise<void> {
		try {
			await this.client.deleteSCIMConfiguration({ orgId, id: configurationId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async createResendInvitation(orgId: string, email: string): Promise<void> {
		try {
			await this.client.createResendInvitation({ orgId, email });
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateMember(member: Member, roleNames: string[]): Promise<Member> {
		try {
			const res = await this.client.updateMember({
				id: member.id,
				orgId: member.accountId,
				memberType: MemberType.WORKSPACE,
				roleNames,
			});
			return res.member!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listWorkspaceTeamAssignments(orgId: string, filter: WorkspaceTeamAssignmentFilter): Promise<WorkspaceTeam[]> {
		try {
			const res = await this.client.listWorkspaceTeamAssignments({ orgId, filter });
			return res.items!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getSharedWorkspaceSetup(id: string): Promise<SharedWorkspaceSetup> {
		try {
			const res = await this.client.getSharedWorkspaceSetup({ id });
			return res.setup!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createSharedWorkspaceSetup(payload: CreateSharedWorkspaceSetupRequest): Promise<SharedWorkspaceSetup> {
		try {
			const resp = await this.client.createSharedWorkspaceSetup(payload);
			return resp.setup!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async submitSharedWorkspaceSetupConfiguration(payload: SubmitSharedWorkspaceSetupConfigurationRequest): Promise<SharedWorkspaceSetup> {
		try {
			const resp = await this.client.submitSharedWorkspaceSetupConfiguration(payload);
			return resp.setup!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async completeSharedWorkspaceSetup(payload: CompleteSharedWorkspaceSetupRequest): Promise<SharedWorkspaceSetup> {
		try {
			const resp = await this.client.completeSharedWorkspaceSetup(payload);
			return resp.setup!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async cancelSharedWorkspaceSetup(payload: CancelSharedWorkspaceSetupRequest): Promise<SharedWorkspaceSetup> {
		try {
			const resp = await this.client.cancelSharedWorkspaceSetup(payload);
			return resp.setup!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listSharedWorkspaceConnections(payload: ListSharedWorkspaceConnectionsRequest): Promise<SharedWorkspaceConnectionList> {
		try {
			const resp = await this.client.listSharedWorkspaceConnections(payload);
			return { connections: resp.items, pageInfo: resp.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async terminateSharedWorkspaceConnection(id: string): Promise<void> {
		try {
			await this.client.terminateSharedWorkspaceConnection({ id });
		} catch (e) {
			throw handleError(e);
		}
	}

	async listSharedWorkspaceSetups(payload: ListSharedWorkspaceSetupsRequest): Promise<SharedWorkspaceSetupList> {
		try {
			const resp = await this.client.listSharedWorkspaceSetups(payload);
			return { setups: resp.items, pageInfo: resp.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}
}

export enum CreateWorkspaceOrTeamInputTypes {
	IMAGE,
	TEXT,
	TOGGLE,
}

export type CreateWorkspaceOrTeamConfigInput =
	| {
			type: CreateWorkspaceOrTeamInputTypes.TEXT;
			name: string;
			label: string;
			required: boolean;
			placeholder: string;
			limit: number;
			multiline: boolean;
	  }
	| {
			name: string;
			type: CreateWorkspaceOrTeamInputTypes.IMAGE;
			description: string;
			helperText: string;
			aspectRatio: number;
	  }
	| {
			name: string;
			type: CreateWorkspaceOrTeamInputTypes.TOGGLE;
			icon: string;
			description: string;
			label: string;
	  };

export type CreateWorkspaceOrTeamConfig = {
	title: string;
	inputGroups: CreateWorkspaceOrTeamConfigInput[][];
}[];
